import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { CommonDataService } from './commonDataService';
import { AppContext } from '../Shared/common';
import { Global } from '../Shared/global';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApplicationConfiguration } from '../Models/ApplicationConfiguration';

export interface ThemeConfig {
  themeName?: string;
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  fontFamily?: string;
  fontSize?: string;
  borderRadius?: string;
  logoUrl?: string;
  faviconUrl?: string;
  appTitle?: string;
  appDescription?: string;
  loginLeftBoxHtml?: string;
  loginLeftCssClass?: string;
  loginFooterHtml?: string;
  customCss?: string;
  [key: string]: any;
}

export interface ThemeDefinition {
  name: string;
  className: string;
  displayName: string;
  description?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  // Available themes
  private availableThemes: ThemeDefinition[] = [
    {
      name: 'fitspeex-dark',
      className: 'theme-fitspeex-dark',
      displayName: 'Dark',
      description: 'Dark theme with cyan accents'
    },
    {
      name: 'red',
      className: 'power-red',
      displayName: 'Red',
      description: 'Bold red theme with light background'
    },
    //{
    //  name: 'violet',
    //  className: 'theme-violet',
    //  displayName: 'Violet',
    //  description: 'Purple gradient theme'
    //},
    //{
    //  name: 'ocean',
    //  className: 'theme-ocean',
    //  displayName: 'Ocean',
    //  description: 'Deep ocean theme with cyan accents'
    //},
    //{
    //  name: 'forest',
    //  className: 'theme-forest',
    //  displayName: 'Forest',
    //  description: 'Forest green theme'
    //},
    //{
    //  name: 'light',
    //  className: 'theme-light',
    //  displayName: 'Light',
    //  description: 'Clean light theme with blue accents'
    //}
  ];

  private currentTheme = new BehaviorSubject<ThemeDefinition>(this.availableThemes[0]);
  public currentTheme$: Observable<ThemeDefinition> = this.currentTheme.asObservable();

  private themeConfig = new BehaviorSubject<ThemeConfig>({});
  public themeConfig$: Observable<ThemeConfig> = this.themeConfig.asObservable();
  
  private isThemeLoaded = false;
  private themeLoadPromise: Promise<ThemeConfig> | null = null;
  private previousThemeClass: string = 'theme-fitspeex-dark'; // Track previous theme

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private commonDataService: CommonDataService,
    private appContext: AppContext
  ) {
    this.initializeStoredTheme();
  }

  /**
   * Initialize theme from local storage
   */
  private initializeStoredTheme(): void {
    const storedThemeName = localStorage.getItem('selectedTheme');
    if (storedThemeName) {
      const theme = this.availableThemes.find(t => t.name === storedThemeName);
      if (theme) {
        this.currentTheme.next(theme);
        this.previousThemeClass = theme.className;
      }
    }
  }

  /**
   * Load theme configuration from API
   */
  loadThemeConfig(): Promise<ThemeConfig> {
    if (this.themeLoadPromise) {
      return this.themeLoadPromise;
    }

    if (this.isThemeLoaded) {
      return Promise.resolve(this.themeConfig.value);
    }

    this.themeLoadPromise = this.fetchAndApplyTheme();
    return this.themeLoadPromise;
  }

  /**
   * Fetch theme config from API and apply it
   */
  private fetchAndApplyTheme(): Promise<ThemeConfig> {
    debugger;
    const endpoint = Global.BASE_USER_ENDPOINT() + 'Application/GetApplicationConfig';

    return this.commonDataService.getData(endpoint)
      .then((config: ApplicationConfiguration) => {
        Global.Log('[ThemeService] Theme config received:', config);

        if (config && config.CustomSettings) {
          const themeConfig: ThemeConfig = this.parseThemeConfig(config.CustomSettings);
          
          // Apply theme name from config if provided
          if (config.CustomSettings.ThemeName) {
            this.setTheme(config.CustomSettings.ThemeName);
          } else {
            this.applyCurrentTheme();
          }

          this.themeConfig.next(themeConfig);
          this.isThemeLoaded = true;
          this.appContext.AppConfigurations = config;
          return themeConfig;
        }

        this.applyCurrentTheme();
        this.isThemeLoaded = true;
        return {};
      })
      .catch(error => {
        Global.LogError('[ThemeService] Error loading theme:', error);
        this.applyCurrentTheme();
        this.isThemeLoaded = true;
        return {};
      });
  }

  /**
   * Parse theme configuration from custom settings
   */
  private parseThemeConfig(customSettings: any): ThemeConfig {
    return {
      themeName: customSettings.ThemeName,
      primaryColor: customSettings.PrimaryColor,
      secondaryColor: customSettings.SecondaryColor,
      accentColor: customSettings.AccentColor,
      fontFamily: customSettings.FontFamily,
      fontSize: customSettings.FontSize,
      borderRadius: customSettings.BorderRadius,
      logoUrl: customSettings.LogoUrl,
      faviconUrl: customSettings.FaviconUrl,
      appTitle: customSettings.AppTitle,
      appDescription: customSettings.AppDescription,
      loginLeftBoxHtml: customSettings.LoginLeftBoxHtml,
      loginLeftCssClass: customSettings.LoginLeftCssClass,
      loginFooterHtml: customSettings.LoginFooterHtml,
      customCss: customSettings.CustomCss
    };
  }

  /**
   * Set theme by name
   */
  setTheme(themeName: string): void {
    const theme = this.availableThemes.find(t => t.name === themeName || t.className === themeName);
    
    if (theme) {
      this.applyTheme(theme);
      this.currentTheme.next(theme);
      localStorage.setItem('selectedTheme', theme.name);
      Global.Log('[ThemeService] Theme changed to:', theme.displayName);
    } else {
      Global.LogWarning('[ThemeService] Theme not found:', themeName);
    }
  }

  /**
   * Apply current theme
   */
  private applyCurrentTheme(): void {
    const currentTheme = this.currentTheme.value;
    this.applyTheme(currentTheme);
  }

  /**
   * Apply theme to the document
   */
  private applyTheme(theme: ThemeDefinition): void {
    // Remove previous theme class
    if (this.previousThemeClass) {
      this.document.documentElement.classList.remove(this.previousThemeClass);
    }

    // Add new theme class
    this.document.documentElement.classList.add(theme.className);
    this.previousThemeClass = theme.className;

    // Also set on body for broader compatibility
    this.document.body.classList.remove(...this.availableThemes.map(t => t.className));
    this.document.body.classList.add(theme.className);

    Global.Log('[ThemeService] Theme applied:', theme.displayName);
  }

  /**
   * Get all available themes
   */
  getAvailableThemes(): ThemeDefinition[] {
    return [...this.availableThemes];
  }

  /**
   * Get current theme
   */
  getCurrentTheme(): ThemeDefinition {
    return this.currentTheme.value;
  }

  /**
   * Get current theme config
   */
  getThemeConfig(): ThemeConfig {
    return this.themeConfig.value;
  }

  /**
   * Check if theme is loaded
   */
  isLoaded(): boolean {
    return this.isThemeLoaded;
  }

  /**
   * Reset theme to defaults
   */
  resetTheme(): void {
    this.isThemeLoaded = false;
    this.themeLoadPromise = null;
    this.themeConfig.next({});
    this.setTheme('fitspeex-dark');
  }
}
